import { defineStore } from "pinia";
import { useOidcStore } from "vue3-oidc";
import { useRepo } from "pinia-orm";
import User from "@/models/User.js";
import { agenciesApi, userApi } from "@/setup/openApiConfigs";
import { undefinedToNull } from "@/utils/utils";

export const useUserStore = defineStore("user", {
  state: () => ({
    userProfile: null,
  }),
  getters: {
    // Delete/Archive permissions - DQS, CQS, Admin can delete/archive any audit
    canDeleteAudit: (state) => {
      const groups = state.userProfile.groups || [];
      return groups.includes("DQS") || groups.includes("CQS") || groups.includes("Developer");
    },

    canArchiveAudit: (state) => {
      const groups = state.userProfile.groups || [];
      return groups.includes("DQS") || groups.includes("CQS") || groups.includes("Developer");
    },

    canCreateAudits: (state) => {
      const groups = state.userProfile.groups || [];
      if (groups.includes("CQS")) return false; // CQS cannot create audits
      return (
        groups.includes("Developer") ||
        groups.includes("DQS") ||
        groups.includes("Manager") ||
        groups.includes("Auditor")
      );
    },
  },

  actions: {
    setUserProfile(profile) {
      if (profile) {
        this.userProfile = {
          ...profile,

          // Role checking functions
          isAuditor() {
            return profile.groups?.includes("Auditor") || false;
          },

          isAuditorNotHigher() {
            // CQS permissions take priority over Auditor role restrictions
            return (
              profile.groups?.includes("Auditor") &&
              !profile.groups?.includes("CQS") &&
              !profile.groups?.includes("Manager") &&
              !profile.groups?.includes("DQS") &&
              !profile.groups?.includes("Developer")
            );
          },

          isDQS() {
            return profile.groups?.includes("DQS") || false;
          },

          isManager() {
            // The Manager role is exclusive with DQS and CQS, QS roles have higher privileges/restrictions
            if (this.isDQS() || this.isCQS()) return false;
            return profile.groups?.includes("Manager") || false;
          },

          isCQS() {
            return profile.groups?.includes("CQS") || false;
          },

          isAdmin() {
            return profile.groups?.includes("Developer") || false;
          },

          hasRole(role) {
            return profile.groups?.includes(role) || false;
          },
        };
      } else {
        this.userProfile = null;
      }
    },

    async fetchCurrentUser() {
      const oidcStore = useOidcStore();
      const userRepo = useRepo(User);
      const { usermanagementUuid, email } = oidcStore.state.value.user.profile;
      const user = undefinedToNull(await userApi.retrieve({ email }));

      let agencyResponse = {};
      if (user.umAgencyUuid) {
        agencyResponse = await agenciesApi.retrieve({
          uuid: user.umAgencyUuid,
        });
      }

      const data = {
        ...user,
        agencyGroup: agencyResponse.agencyGroup?.uuid,
        society: agencyResponse.agencyGroup?.society,
        businessUnit: agencyResponse.agencyGroup?.businessUnit,
        usermanagementUuid: usermanagementUuid,
      };

      this.setUserProfile(data);
      return userRepo.save({
        ...data,
        isCurrentUser: true,
      });
    },
  },
});
